import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Button, FormControl, Grid, InputLabel, MenuItem, Select, Typography } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';

import { Moment } from 'moment';

import {
	NfcCardTransactionCriteria,
	NfcCardTransactionType,
	emptyNfcCardTransactionCriteria,
	isNfcCardTransactionType,
	nfcCardTransactionTypes,
} from 'lib/models/nfcCards';

import useNfcCardLabels from 'hooks/useNfcCardLabels';

import FilterAccordion from 'components/FilterAccordion';

type Props = {
	onSearch: (criteria: NfcCardTransactionCriteria) => void;
	loading?: boolean;
};

/** What "no filter" is worth in a MUI select, which cannot hold undefined. */
const ALL = '';

/** What the operator picked, before it is a search. */
type Draft = {
	type: NfcCardTransactionType | '';
	from: Moment | null;
	to: Moment | null;
};

const emptyDraft: Draft = { type: ALL, from: null, to: null };

/**
 * The movements of a card narrowed by kind and by days, in the same draft-then-search way as
 * the wallet operations filters: nothing changes until the operator presses Buscar.
 *
 * The days are local days, sent as the pair of instants that bound them here.
 */
export default function NfcCardTransactionsFilters({ onSearch, loading }: Props) {
	const { t } = useTranslation();
	const { transactionTypeLabel } = useNfcCardLabels();

	const [draft, setDraft] = useState<Draft>(emptyDraft);

	// A picker being typed into holds a date that is not one yet
	const brokenDate = !!(draft.from && !draft.from.isValid()) || !!(draft.to && !draft.to.isValid());

	const broken = brokenDate
		? t('nfc:transactionsDateInvalid')
		: draft.from && draft.to && draft.from.isAfter(draft.to, 'day')
			? t('nfc:transactionsDateRangeInvalid')
			: '';

	function search(next: Draft) {
		const { from, to } = next;

		onSearch({
			...(next.type ? { type: next.type } : {}),
			// Cloned before being moved: moment mutates in place
			...(from?.isValid() ? { from: from.clone().startOf('day').toISOString() } : {}),
			...(to?.isValid() ? { to: to.clone().endOf('day').toISOString() } : {}),
		});
	}

	function clear() {
		setDraft(emptyDraft);
		onSearch(emptyNfcCardTransactionCriteria);
	}

	return (
		<FilterAccordion
			title={t('nfc:transactionsFilters')}
			onClear={clear}
			clearTooltip={t('nfc:transactionsFiltersClear')}
			defaultExpanded={false}
		>
			<Box
				component="form"
				onSubmit={(event) => {
					event.preventDefault();
					if (!broken) search(draft);
				}}
			>
				<Grid container spacing={2}>
					<Grid item xs={12} sm={6} md={3}>
						<FormControl fullWidth>
							<InputLabel id="nfc-transaction-type">{t('nfc:transactionType')}</InputLabel>
							<Select
								labelId="nfc-transaction-type"
								id="nfc-transaction-type-select"
								label={t('nfc:transactionType')}
								value={draft.type}
								onChange={(event) => {
									const value = event.target.value;
									setDraft({ ...draft, type: isNfcCardTransactionType(value) ? value : ALL });
								}}
							>
								<MenuItem value={ALL}>{t('nfc:transactionsFilterAll')}</MenuItem>
								{nfcCardTransactionTypes.map((type) => (
									<MenuItem key={type} value={type}>
										{transactionTypeLabel(type)}
									</MenuItem>
								))}
							</Select>
						</FormControl>
					</Grid>

					<Grid item xs={12} sm={6} md={3}>
						<DatePicker
							sx={{ width: '100%' }}
							value={draft.from}
							label={t('nfc:transactionsFilterFrom')}
							format="DD/MM/YYYY"
							onChange={(value) => setDraft({ ...draft, from: value })}
						/>
					</Grid>

					<Grid item xs={12} sm={6} md={3}>
						<DatePicker
							sx={{ width: '100%' }}
							value={draft.to}
							minDate={draft.from || undefined}
							label={t('nfc:transactionsFilterTo')}
							format="DD/MM/YYYY"
							onChange={(value) => setDraft({ ...draft, to: value })}
						/>
					</Grid>

					<Grid item xs={12} sm={6} md={3} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
						<Button type="submit" variant="contained" disabled={!!broken || loading}>
							{t('common:search')}
						</Button>
						<Button onClick={clear} disabled={loading}>
							{t('nfc:transactionsFiltersClear')}
						</Button>
					</Grid>

					<Grid item xs={12}>
						<Typography variant="caption" color={broken ? 'error' : 'text.secondary'}>
							{broken || t('nfc:transactionsFiltersHelp')}
						</Typography>
					</Grid>
				</Grid>
			</Box>
		</FilterAccordion>
	);
}
